import { useEffect, useState } from "react";
import type { BookingStatus } from "@gigflow/shared";
import { bookingStatuses } from "@gigflow/shared";
import { apiRequest } from "../api";
import { useAuth } from "../auth";

type Booking = {
  id: string;
  status: BookingStatus;
  preferredStartsAt: string;
  addressLine1: string;
  city: string;
  region: string;
  clientNotes?: string | null;
  service?: { name: string };
  client?: { user?: { fullName?: string; email?: string } };
};

const OWNER_ACTIONS: BookingStatus[] = [
  "CONFIRMED",
  "IN_PROGRESS",
  "COMPLETED",
  "CANCELLED",
  "REJECTED",
];

export function BookingsPage() {
  const { businessId } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function load() {
    if (!businessId) return;
    const data = await apiRequest<{ bookings: Booking[] }>(
      `/businesses/${businessId}/bookings`,
    );
    setBookings(data.bookings);
  }

  useEffect(() => {
    void load().catch((err: Error) => setError(err.message));
  }, [businessId]);

  async function updateStatus(bookingId: string, status: BookingStatus) {
    if (!businessId) return;
    setPending(true);
    setError(null);
    try {
      await apiRequest(
        `/businesses/${businessId}/bookings/${bookingId}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ status }),
        },
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Status update failed");
    } finally {
      setPending(false);
    }
  }

  if (!businessId)
    return (
      <section>
        <p className="notice">No business selected.</p>
      </section>
    );

  return (
    <>
      <header className="page-header">
        <div>
          <p className="eyebrow">Bookings</p>
          <h1>Manage booking requests</h1>
        </div>
      </header>

      {error ? <p className="notice error">{error}</p> : null}

      <section>
        <table className="data-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Service</th>
              <th>When</th>
              <th>Location</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((booking) => (
              <tr key={booking.id}>
                <td>
                  {booking.client?.user?.fullName ?? "—"}
                  <div className="notice">{booking.client?.user?.email}</div>
                </td>
                <td>{booking.service?.name ?? "—"}</td>
                <td>{new Date(booking.preferredStartsAt).toLocaleString()}</td>
                <td>
                  {booking.addressLine1}
                  <div className="notice">
                    {booking.city}, {booking.region}
                  </div>
                </td>
                <td>{booking.status}</td>
                <td className="actions">
                  {OWNER_ACTIONS.filter((s) => s !== booking.status).map(
                    (status) => (
                      <button
                        key={status}
                        className="btn btn-ghost"
                        type="button"
                        disabled={pending}
                        onClick={() => void updateStatus(booking.id, status)}
                      >
                        {status.replace("_", " ")}
                      </button>
                    ),
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {bookings.length === 0 ? (
          <p className="notice">No bookings yet.</p>
        ) : null}
        <p className="notice">Statuses: {bookingStatuses.join(", ")}</p>
      </section>
    </>
  );
}
